/*
  card-product-custom.js
  Powers snippets/card-product-custom.liquid. Scoped per [data-product-card]
  so any number of cards can sit on one page (collection grid, carousel).

  This never touches /cart/add.js, cart rendering, or discount math directly.
  The Add to Cart button lives inside a real <product-form> / native form
  (form_type=product); Dawn's own product-form.js (already loaded globally)
  owns the actual submit + cart-drawer render. This file only:
    - drives the dropdown open/select interaction,
    - swaps the pre-rendered per-row data (from each <li>'s data-row
      attribute — no text parsing) into the trigger, badge slots, and
      price/plan fields,
    - keeps the hidden `id` / `selling_plan` form inputs in sync with the
      current selection, and
    - swaps the hero image to the selected variant's own image (falling
      back to the product's featured image), and
    - hides the Add to cart button and shows the "Backorder" badge while
      the selected variant is on backorder (the variant's own backorder
      metafield wins; the product's applies only when the variant has no
      entry), and
    - hides the Add to cart button while the selected variant is sold out.
      Sold-out rows (aria-disabled) can't be picked, so that only happens
      for the default of a product with no available variants.
*/

(function () {
  function rowData(option) {
    try {
      return JSON.parse(option.getAttribute('data-row'));
    } catch (e) {
      return null;
    }
  }

  function setupCard(card) {
    var uid = card.getAttribute('data-uid');
    var metaScript = uid ? document.querySelector('script[data-card-json="' + uid + '"]') : null;
    var cardMeta = null;
    if (metaScript) {
      try {
        cardMeta = JSON.parse(metaScript.textContent);
      } catch (e) {
        cardMeta = null;
      }
    }

    var selectorRoot = card.querySelector('[data-variant-selector]');
    var trigger = selectorRoot && selectorRoot.querySelector('[data-variant-trigger]');
    var listbox = selectorRoot && selectorRoot.querySelector('[role="listbox"]');
    var chevron = selectorRoot && selectorRoot.querySelector('[data-variant-chevron]');
    var options = listbox
      ? Array.prototype.slice.call(listbox.querySelectorAll('li[role="option"]'))
      : [];

    var slots = {
      title: card.querySelector('[data-selected-title]'),
      qty: card.querySelector('[data-selected-qty]'),
      price: card.querySelector('[data-selected-price]'),
      icon: card.querySelector('[data-selected-icon]'),
    };

    var badgeTop = card.querySelector('[data-badge-slot="top"]');
    var badgeBottom = card.querySelector('[data-badge-slot="bottom"]');
    var variantIdInput = card.querySelector('[data-variant-id-input]');
    var sellingPlanInput = card.querySelector('[data-selling-plan-input]');
    var onetimePriceEl = card.querySelector('[data-price-onetime]');
    var subscribePriceEl = card.querySelector('[data-price-subscribe]');
    var onetimeRadio = card.querySelector('[data-plan-onetime]');
    var subscribeRadio = card.querySelector('[data-plan-subscribe]');
    var onetimeLabel = onetimeRadio ? onetimeRadio.closest('.pc__plan') : null;
    var subscribeLabel = subscribeRadio ? subscribeRadio.closest('.pc__plan') : null;
    var addButton = card.querySelector('[data-add-to-cart]');
    var heroImage = card.querySelector('[data-hero-image]');

    // Shows the selected variant's own image, or the product's featured
    // image when the variant has none.
    function applyHeroImage(row) {
      if (!heroImage) return;
      var image = (row && row.image) || (cardMeta && cardMeta.productImage);
      if (!image || !image.src) return;
      if (heroImage.getAttribute('src') !== image.src) heroImage.setAttribute('src', image.src);
      heroImage.alt = image.alt || '';
      if (image.focalX != null && image.focalY != null) {
        heroImage.style.setProperty('--pc-focal-x', image.focalX + '%');
        heroImage.style.setProperty('--pc-focal-y', image.focalY + '%');
      } else {
        heroImage.style.removeProperty('--pc-focal-x');
        heroImage.style.removeProperty('--pc-focal-y');
      }
    }

    // The selected variant's own backorder entry (true OR false) wins;
    // the product's applies only when the variant has no entry (null).
    function isBackordered(row) {
      if (row && row.backorder != null) return !!row.backorder;
      return !!(cardMeta && cardMeta.productBackorder);
    }

    // Same precedence as backorder: variant label, else product label.
    function resolveLabel(row) {
      return (row && row.label) || (cardMeta && cardMeta.productLabel) || '';
    }

    // Mirrors the slot rules in card-product-custom.liquid — keep in sync.
    // Each slot is { text, kind } where kind drives the pill style.
    function resolveBadgeSlots(row) {
      var strength = (cardMeta && cardMeta.strength) || '';
      var label = resolveLabel(row);
      var top = null;
      var bottom = null;
      if (isBackordered(row)) {
        top = { text: 'Backorder', kind: 'backorder' };
        if (strength) bottom = { text: strength, kind: 'strength' };
        else if (label) bottom = { text: label, kind: 'label' };
      } else if (label) {
        top = { text: label, kind: 'label' };
        if (strength) bottom = { text: strength, kind: 'strength' };
      } else if (strength) {
        top = { text: strength, kind: 'strength' };
      }
      return { top: top, bottom: bottom };
    }

    function fillBadgeSlot(el, slot) {
      if (!el) return;
      el.textContent = slot ? slot.text : '';
      el.hidden = !slot;
      el.classList.toggle('pc__badge--outline', !!slot && slot.kind === 'label');
    }

    function applyBackorderState(row) {
      // Hiding the button leaves Details as the only item in
      // .pc__actions, so it stretches to full width.
      if (addButton) addButton.hidden = isBackordered(row) || (row && row.available === false);
      var badges = resolveBadgeSlots(row);
      fillBadgeSlot(badgeTop, badges.top);
      fillBadgeSlot(badgeBottom, badges.bottom);
    }

    function selectRow(option) {
      if (!option) return;
      var row = rowData(option);
      if (!row) return;

      options.forEach(function (item) {
        item.setAttribute('aria-selected', String(item === option));
      });

      if (slots.title) slots.title.textContent = row.title || '';
      if (slots.qty) slots.qty.textContent = row.qty || '';
      if (slots.price) slots.price.textContent = row.price || '';
      if (slots.icon) {
        // Mirror the row's own strain icon (rendered by
        // card-product-custom-row); rows without one hide the slot.
        var rowIcon = option.querySelector('.pc__option-icon');
        slots.icon.innerHTML = rowIcon ? rowIcon.innerHTML : '';
        slots.icon.hidden = !rowIcon;
      }

      if (variantIdInput) variantIdInput.value = row.variantId;

      var hasPlan = !!row.sellingPlanId;
      if (onetimeLabel) onetimeLabel.classList.toggle('is-checked', !hasPlan);
      if (subscribeLabel) {
        subscribeLabel.classList.toggle('pc__plan--hidden', !hasPlan);
        subscribeLabel.classList.toggle('is-checked', hasPlan);
        subscribeLabel.setAttribute('aria-hidden', hasPlan ? 'false' : 'true');
      }
      if (onetimeRadio) onetimeRadio.checked = !hasPlan;
      if (subscribeRadio) {
        subscribeRadio.disabled = !hasPlan;
        subscribeRadio.checked = hasPlan;
      }
      if (onetimePriceEl) onetimePriceEl.textContent = row.price || '';
      if (subscribePriceEl) subscribePriceEl.textContent = hasPlan ? row.subscribePrice || '' : '';

      if (sellingPlanInput) {
        if (hasPlan) {
          sellingPlanInput.removeAttribute('disabled');
          sellingPlanInput.value = row.sellingPlanId;
        } else {
          sellingPlanInput.setAttribute('disabled', 'disabled');
          sellingPlanInput.value = '';
        }
      }

      applyBackorderState(row);
      applyHeroImage(row);
    }

    function setOpen(open) {
      if (!listbox) return;
      listbox.hidden = !open;
      if (chevron) chevron.classList.toggle('pc__variant-chevron--open', open);
      if (trigger) trigger.setAttribute('aria-expanded', String(open));
    }

    function isOpen() {
      return !!listbox && !listbox.hidden;
    }

    // Scrolls the listbox itself (not scrollIntoView, which would also
    // scroll the page/carousel behind it).
    function revealSelected() {
      if (!listbox) return;
      var selected = listbox.querySelector('li[aria-selected="true"]');
      if (!selected) return;
      var top = selected.offsetTop;
      var bottom = top + selected.offsetHeight;
      if (top < listbox.scrollTop) {
        listbox.scrollTop = top;
      } else if (bottom > listbox.scrollTop + listbox.clientHeight) {
        listbox.scrollTop = bottom - listbox.clientHeight;
      }
    }

    if (trigger) {
      trigger.addEventListener('click', function (event) {
        event.stopPropagation();
        setOpen(!isOpen());
        if (isOpen()) revealSelected();
      });
    }

    options.forEach(function (option) {
      option.addEventListener('click', function (event) {
        // Sold-out rows stay listed but can't be selected; keep the
        // dropdown open so the shopper can pick another row.
        if (option.getAttribute('aria-disabled') === 'true') {
          event.stopPropagation();
          return;
        }
        selectRow(option);
        setOpen(false);
        if (trigger) trigger.focus();
      });
    });

    document.addEventListener('click', function (event) {
      if (isOpen() && selectorRoot && !selectorRoot.contains(event.target)) {
        setOpen(false);
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && isOpen()) {
        setOpen(false);
        if (trigger) trigger.focus();
      }
    });

    function checkOnetime() {
      if (!onetimeRadio || onetimeRadio.disabled) return;
      onetimeRadio.checked = true;
      if (onetimeLabel) onetimeLabel.classList.add('is-checked');
      if (subscribeLabel) subscribeLabel.classList.remove('is-checked');
      if (sellingPlanInput) sellingPlanInput.setAttribute('disabled', 'disabled');
    }

    function checkSubscribe() {
      if (!subscribeRadio || subscribeRadio.disabled) return;
      subscribeRadio.checked = true;
      if (subscribeLabel) subscribeLabel.classList.add('is-checked');
      if (onetimeLabel) onetimeLabel.classList.remove('is-checked');
      if (sellingPlanInput) sellingPlanInput.removeAttribute('disabled');
    }

    if (onetimeLabel) onetimeLabel.addEventListener('click', checkOnetime);
    if (subscribeLabel) subscribeLabel.addEventListener('click', checkSubscribe);

    if (options.length) {
      var defaultIndex = cardMeta && typeof cardMeta.defaultRowIndex === 'number' ? cardMeta.defaultRowIndex : 0;
      selectRow(options[defaultIndex] || options[0]);
    }

    setOpen(false);
  }

  function init() {
    document.querySelectorAll('[data-product-card]').forEach(function (card) {
      if (card.hasAttribute('data-pc-initialized')) return;
      card.setAttribute('data-pc-initialized', 'true');
      setupCard(card);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-scan when a carousel/section re-renders cards after this file first
  // ran (e.g. an app block, infinite scroll, or a section re-render).
  document.addEventListener('shopify:section:load', init);

  // Re-scan when <product-recommendations> (global.js) swaps in cards via
  // AJAX — that injection never fires shopify:section:load, so without this
  // the related-products cards never get their dropdown/data wired up.
  document.addEventListener('product-recommendations:loaded', init);

  // Re-scan when the collection grid is re-rendered via AJAX by facets.js
  // (sorting, filtering, price range, back/forward navigation).
  document.addEventListener('facets:products-rendered', init);
})();
